import { SxProps } from '@mui/material';
import { Theme } from '@mui/system';

import {
  IconButton,
  IconButtonProps,
} from '@/components/form/baseInputs/Button/IconButton';
import { SearchSmIcon } from '@/components/icons/SearchSmIcon';
import { COLORS } from '@/styles/tokens/colors';

// Styled locally rather than through a shared Button `variant` so changes to the
// global success button variants don't alter this tiny 16px affordance. Matches
// the soft tinted treatment of the accept suggestion button.
const locateButtonSx = {
  width: '16px',
  height: '16px',
  minWidth: '16px',
  p: '2px',
  border: 'none',
  backgroundColor: COLORS.FUNCTIONAL.SUCCESS[100],
  color: COLORS.FUNCTIONAL.SUCCESS[700],
  '&:hover': {
    backgroundColor: COLORS.FUNCTIONAL.SUCCESS[200],
  },
  '&:disabled': {
    backgroundColor: COLORS.GRAY[100],
    color: COLORS.GRAY[400],
  },
};

interface LocateSuggestionButtonProps
  extends Pick<IconButtonProps, 'disabled' | 'iconProps'> {
  suggestionID: string;
  onLocateSuggestionClick: (s: string) => void;
  ariaLabel?: string;
  dataTestId?: string;
  sx?: SxProps<Theme>;
}

/**
 * Generic IconButton with a magnifying glass icon to locate evidence for a suggestion.
 *
 * @param suggestionID
 * @param onLocateSuggestionClick
 * @param ariaLabel
 * @param dataTestId
 * @param sx
 * @param iconProps
 * @param disabled
 * @constructor
 */
export function LocateSuggestionButton({
  suggestionID,
  onLocateSuggestionClick,
  ariaLabel = 'Locate suggestion',
  dataTestId = 'onboarding-suggestion-row-search-button',
  sx = {},
  iconProps = {},
  disabled,
}: LocateSuggestionButtonProps) {
  return (
    <IconButton
      icon={SearchSmIcon}
      iconProps={{
        size: 12,
        fill: 'transparent',
        ...iconProps,
      }}
      ariaLabel={ariaLabel}
      size="xs"
      variant="transparent"
      onClick={() => onLocateSuggestionClick(suggestionID)}
      disabled={disabled}
      sx={{
        ...locateButtonSx,
        ...sx,
      }}
      data-testid={dataTestId}
    />
  );
}
